import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { fetchWithAuth, postWithUsername } from "../services/fetchWrapper";
import { useAuth } from "../utils/AuthContext";
import { Environment } from "../utils/Environment";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";

const apiUrl = Environment.getApiUrl();

const REQUIRED_TABLES = [
    "allmeters",
    "allpipes",
    "critical_large_user",
    "criticality_results",
    "criticalsegments",
    // "meters_by_segment",
    "outagesegments",
    "outagemeters",
    "criticalmeters",
    "criticalvalves"
];

const TABLE_DESCRIPTIONS: { [key: string]: string } = {
    allmeters: "All meters in the hydraulic system.",
    allpipes: "All pipes in the hydraulic system.",
    critical_large_user: "List of large users where it will be critical if they lose service.",
    criticality_results: "Results of the criticality analysis.",
    criticalsegments: "Segments identified as critical.",
    // meters_by_segment: "Mapping of meters to segments.",
    outagesegments: "Segments affected by outages.",
    outagemeters: "Meters affected by outages.",
    criticalmeters: "Meters identified as critical.",
    criticalvalves: "Valves identified as critical.",
};

const UploadHydroService: React.FC = () => {
    const { isLoggedIn, userName } = useAuth();
    const [uploadResult, setUploadResult] = useState<any>(null);
    const [form, setForm] = useState({
        client_id: "",
        client_name: "",
        base_dataset: "false",
    });
    const [selectedTable, setSelectedTable] = useState(REQUIRED_TABLES[0]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const username = localStorage.getItem("userName");
                if (!username) {
                    setLoading(false);
                    return;
                }
                const response = await postWithUsername(`${apiUrl}/get-client-data`, username);
                const data = await response.json();
                setForm((prevForm) => ({
                    ...prevForm,
                    client_id: data.client_id,
                    client_name: data.client_name
                }));
                // Set latest job_id in localStorage
                if (data.job_id) {
                    localStorage.setItem("job_id", data.job_id);
                }
            } catch (error) {
                alert("Failed to fetch client data. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleTableChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedTable(e.target.value);
        setSelectedFile(null); // reset file when table changes
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null);
    };

    const handleDeleteTable = async (tableName: string) => {
        const job_id = localStorage.getItem("job_id") || "";
        const client_id = form.client_id;
        const username = userName;

        if (!job_id || !client_id || !username) {
            alert("Missing job, client, or user information.");
            return;
        }

        if (!window.confirm(`Are you sure you want to delete data for "${tableName}"? This cannot be undone.`)) {
            return;
        }

        setUploading(true);
        try {
            const response = await fetchWithAuth(`${apiUrl}/remove-one-table-data`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    job_id,
                    client_id,
                    table_name: tableName,
                    username,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                alert(data.detail || "Failed to delete table data.");
            } else {
                alert(data.message || "Table data deleted successfully.");
                setUploadResult(null); // Optionally clear upload result
            }
        } catch (err) {
            alert("Failed to delete table data.");
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile) {
            alert(`Please upload a file for ${selectedTable}`);
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("excel_files", selectedFile);
            if (userName) {
                formData.append("username", userName);
            } else {
                alert("Username is missing. Please log in again.");
                setUploading(false);
                return;
            }
            formData.append("client_id", form.client_id);
            formData.append("client_name", form.client_name);
            formData.append("job_id", localStorage.getItem("job_id") || "");
            formData.append("base_dataset", form.base_dataset);
            formData.append("table_name", selectedTable);

            const response = await fetchWithAuth(`${apiUrl}/create-hydrojob-and-import-data`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                // Try to extract backend error message
                let errorMsg = "Upload failed";
                try {
                    const errorData = await response.json();
                    if (errorData.detail) {
                        errorMsg = errorData.detail;
                    }
                } catch {
                    // fallback to default error message
                }
                alert(errorMsg);
                setUploading(false);
                return;
            }

            const data = await response.json();
            setUploadResult(data);
            alert("Upload successful!");
        } catch (err) {
            alert("Upload failed");
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="h6">Loading...</Typography>
            </Box>
        );
    }

    if (!isLoggedIn) {
        return (
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography variant="h6" color="error">
                    You must be logged in to access this page.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    pt: 6,
                }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Required Tables for Hydraulic Processing:
                </Typography>
                <List dense sx={{ pl: 1 }}>
                    {REQUIRED_TABLES.map(tableName => (
                        <ListItem key={tableName} sx={{ py: 0 }}>
                            <ListItemText
                                primary={
                                    <span style={{ fontSize: "0.95em" }}>
                                        <strong>{tableName}</strong>: {TABLE_DESCRIPTIONS[tableName]}
                                    </span>
                                }
                                primaryTypographyProps={{ variant: "caption" }}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
            <form
                onSubmit={handleSubmit}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    maxWidth: 500,
                    width: "100%",
                }}
            >
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Upload Hydraulic Data Table
                </Typography>
                <TextField
                    select
                    label="Select Table"
                    value={selectedTable}
                    onChange={handleTableChange}
                    sx={{ mb: 2 }}
                >
                    {REQUIRED_TABLES.map(tableName => (
                        <MenuItem key={tableName} value={tableName}>
                            {tableName}
                        </MenuItem>
                    ))}
                </TextField>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                    {TABLE_DESCRIPTIONS[selectedTable]}
                </Typography>
                <input
                    type="file"
                    accept=".csv, .xls, .xlsx, .mdb, .accdb"
                    onChange={handleFileChange}
                    required
                />
                {/* Hidden Fields */}
                <input type="hidden" name="client_id" value={form.client_id} />
                <input type="hidden" name="client_name" value={form.client_name} />

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                        mt: 2,
                        textTransform: "none",
                    }}
                    disabled={uploading}
                >
                    Upload
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    sx={{ mt: 2 }}
                    onClick={() => handleDeleteTable(selectedTable)}
                    disabled={uploading || loading}
                >
                    Delete Data for Selected Table
                </Button>
            </form>
            {uploading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <CircularProgress />
                </Box>
            )}
            {uploadResult && (
                <Box sx={{ mt: 2, p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
                    <Typography variant="h6">Upload Result</Typography>
                    <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(uploadResult, null, 2)}</pre>
                </Box>
            )}
        </Box>
    );
};

export default UploadHydroService;